import { helper } from '@ember/component/helper';
import { assert } from '@ember/debug';

/**
 * Ember helper function to sort an array of models by a specified attribute path.
 *
 * @param positional - An array containing two elements:
 *   1. The array of models to be sorted.
 *   2. The attribute path (string) to sort by. Prefix with '-' for descending order.
 * @returns The sorted array of models, or the original array if input is invalid.
 *
 * @throws Will assert if the number of positional parameters is not 2.
 * @throws Will assert if the attribute path does not resolve to a non-nullish value on the first model.
 *
 * @example
 * // Sort by 'name' ascending
 * {{sort @models "name"}}
 *
 * // Sort by 'age' descending
 * {{sort @models "-age"}}
 *
 * // Sort by nested property 'profile.lastName'
 * {{sort @models "profile.lastName"}}
 */
export default helper(function sort(positional /*, named*/) {
  assert(
    `Es wurden nicht 2 Positional Parameter übergeben, sondern ${positional.length} (sort @models "sortAttribute")`,
    positional.length == 2
  );
  if (positional.length != 2) {
    return positional;
  }

  if (!positional[0] || positional[0].length == 0) {
    return positional[0];
  }

  const models = positional[0].map((item) => item);

  let accessPath = positional[1] as string;
  const desc = accessPath?.[0] === '-';
  if (desc) {
    accessPath = accessPath.slice(1);
  }
  assert(
    `sort: getting \`${accessPath}\` from ${models[0]} is nullish: ${deep_value(models[0], accessPath)}`,
    deep_value(models[0], accessPath) !== null &&
      deep_value(models[0], accessPath) !== undefined
  );
  const typeOfValue = typeof deep_value(models[0], accessPath);

  if (typeOfValue === 'string') {
    models.sort((a, b) =>
      deep_value(a, accessPath)?.localeCompare(deep_value(b, accessPath))
    );
    if (desc) {
      models.reverse();
    }
  } else {
    const mult = desc ? -1 : 1;
    models.sort(
      (a, b) => (deep_value(a, accessPath) - deep_value(b, accessPath)) * mult
    );
  }
  // debug

  return models;
});

const deep_value = (obj: any, path: string) =>
  path.split('.').reduce((current, nextPath) => current?.[nextPath], obj);
